/**
 * Import utility functions for menu import functionality
 * Optimized for millisecond-level performance
 */

import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

/**
 * Creates a filtered CSV file with only selected rows
 * @param {Array} allRows - All parsed rows from original file
 * @param {Set|Array} selectedIndices - Indices of selected rows (0-based)
 * @param {string} originalFileName - Original file name for extension
 * @returns {Promise<File>} Filtered CSV file
 */
export const createFilteredCSVFile = async (allRows, selectedIndices, originalFileName = 'filtered_import.csv') => {
  // Convert Set to Array if needed
  const indicesArray = selectedIndices instanceof Set ? Array.from(selectedIndices) : selectedIndices;
  
  // Filter rows to only selected indices
  const filteredRows = indicesArray
    .sort((a, b) => a - b) // Sort indices to maintain original order
    .map(index => allRows[index])
    .filter(Boolean); // Remove any undefined rows
  
  // Get headers from first row (if available) or generate from all keys
  const headers = filteredRows.length > 0 
    ? Object.keys(filteredRows[0])
    : [];
  
  // Create CSV string
  const csv = Papa.unparse(filteredRows, {
    header: true,
    columns: headers
  });
  
  // Create Blob and File
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const fileName = originalFileName.endsWith('.csv') 
    ? originalFileName.replace('.csv', '_filtered.csv')
    : 'filtered_import.csv';
  
  return new File([blob], fileName, { type: 'text/csv' });
};

/**
 * Creates a filtered Excel file with only selected rows
 * @param {Array} allRows - All parsed rows from original file
 * @param {Set|Array} selectedIndices - Indices of selected rows (0-based)
 * @param {string} originalFileName - Original file name for extension
 * @returns {Promise<File>} Filtered Excel file
 */
export const createFilteredExcelFile = async (allRows, selectedIndices, originalFileName = 'filtered_import.xlsx') => {
  // Convert Set to Array if needed
  const indicesArray = selectedIndices instanceof Set ? Array.from(selectedIndices) : selectedIndices;
  
  // Filter rows to only selected indices
  const filteredRows = indicesArray
    .sort((a, b) => a - b) // Sort indices to maintain original order
    .map(index => allRows[index])
    .filter(Boolean); // Remove any undefined rows
  
  // Create workbook
  const wb = XLSX.utils.book_new();
  
  // Convert filtered rows to worksheet
  const ws = XLSX.utils.json_to_sheet(filteredRows);
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Dishes');
  
  // Generate Excel file as buffer
  const excelBuffer = XLSX.write(wb, { 
    type: 'array', 
    bookType: 'xlsx' 
  });
  
  // Create Blob and File
  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const fileName = originalFileName.match(/\.(xlsx|xls)$/i)
    ? originalFileName.replace(/\.(xlsx|xls)$/i, '_filtered.xlsx')
    : 'filtered_import.xlsx';
  
  return new File([blob], fileName, { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
};

/**
 * Creates a filtered file (CSV or Excel) with only selected rows
 * @param {File} originalFile - Original file
 * @param {Array} allRows - All parsed rows from original file
 * @param {Set|Array} selectedIndices - Indices of selected rows (0-based)
 * @returns {Promise<File>} Filtered file
 */
export const createFilteredFile = async (originalFile, allRows, selectedIndices) => {
  const fileName = originalFile.name;
  const extension = fileName.split('.').pop().toLowerCase();
  
  if (extension === 'csv') {
    return createFilteredCSVFile(allRows, selectedIndices, fileName);
  } else if (extension === 'xlsx' || extension === 'xls') {
    return createFilteredExcelFile(allRows, selectedIndices, fileName);
  } else {
    throw new Error(`Unsupported file type: ${extension}`);
  }
};

/**
 * Parses a CSV file asynchronously
 * @param {File} file - CSV file to parse
 * @returns {Promise<Array>} Parsed data array with normalized column names
 */
export const parseCSVFile = (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => {
        // Normalize header names (trim, lowercase, remove spaces and special chars)
        const normalized = header.trim().toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
        // Map to field name if possible
        return mapColumnToField(normalized) || normalized;
      },
      complete: (results) => {
        if (results.errors.length > 0) {
          console.warn('CSV parsing warnings:', results.errors);
        }
        resolve(results.data);
      },
      error: (error) => {
        reject(new Error(`CSV parsing failed: ${error.message}`));
      }
    });
  });
};

/**
 * Parses an Excel file
 * @param {File} file - Excel file to parse
 * @returns {Promise<Array>} Parsed data array
 */
export const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON with header row
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          defval: '', // Default value for empty cells
          raw: false // Convert all values to strings
        });
        
        // Normalize keys and map to field names
        const normalizedData = jsonData.map(row => {
          const normalized = {};
          Object.keys(row).forEach(key => {
            const normalizedKey = key.trim().toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
            // Map to field name if possible, otherwise use normalized key
            const fieldName = mapColumnToField(normalizedKey) || normalizedKey;
            normalized[fieldName] = row[key];
          });
          return normalized;
        });
        
        resolve(normalizedData);
      } catch (error) {
        reject(new Error(`Excel parsing failed: ${error.message}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read Excel file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Maps a CSV/Excel row to dish data structure
 * Handles both export format and simplified format column names
 * @param {Object} row - Row data from CSV/Excel (already normalized by parse functions)
 * @param {Object} context - Context with tags and cuisines maps
 * @returns {Object} Mapped dish data
 */
export const mapRowToDishData = (row, context) => {
  // Find matching tag (O(1) lookup)
  // Try dishTagHy first, then dishTagEn, dishTagRu, or dishCategoryAm, etc.
  let dishTagDto = null;
  const tagValue = row.dishTagHy || row.dishCategoryAm || row.dishCategoryHy || 
                   row.dishTagEn || row.dishCategoryEn || 
                   row.dishTagRu || row.dishCategoryRu;
  
  if (tagValue && context.tagsMap) {
    const tagKey = tagValue.toLowerCase().trim();
    dishTagDto = context.tagsMap.get(tagKey);
    
    // If not found by exact match, try finding by any translation
    if (!dishTagDto && context.tags) {
      for (const tag of context.tags) {
        if (tag.tag && tag.tag.toLowerCase().trim() === tagKey) {
          dishTagDto = tag;
          break;
        }
        if (tag.translations) {
          for (const trans of tag.translations) {
            if (trans.value && trans.value.toLowerCase().trim() === tagKey) {
              dishTagDto = tag;
              break;
            }
          }
          if (dishTagDto) break;
        }
      }
    }
  }

  // Find matching cuisine (O(1) lookup)
  // Try cuisineHy first, then cuisineAm, cuisineEn, cuisineRu
  let dishCuisineTagDto = null;
  const cuisineValue = row.cuisineHy || row.cuisineAm || 
                       row.cuisineEn || row.cuisineRu;
  
  if (cuisineValue && context.cuisinesMap) {
    const cuisineKey = cuisineValue.toLowerCase().trim();
    dishCuisineTagDto = context.cuisinesMap.get(cuisineKey);
    
    // If not found by exact match, try finding by any translation
    if (!dishCuisineTagDto && context.cuisines) {
      for (const cuisine of context.cuisines) {
        if (cuisine.cuisineTag && cuisine.cuisineTag.toLowerCase().trim() === cuisineKey) {
          dishCuisineTagDto = cuisine;
          break;
        }
        if (cuisine.translations) {
          for (const trans of cuisine.translations) {
            if (trans.value && trans.value.toLowerCase().trim() === cuisineKey) {
              dishCuisineTagDto = cuisine;
              break;
            }
          }
          if (dishCuisineTagDto) break;
        }
      }
    }
  }

  // Map ingredients - handle both "ingredientHy" and "ingredientsAm" formats
  const ingredientHy = row.ingredientHy || row.ingredientsAm || row.ingredientsHy || '';
  const ingredientEn = row.ingredientEn || row.ingredientsEn || '';
  const ingredientRu = row.ingredientRu || row.ingredientsRu || '';
  
  const ingridientsDto = [{
    ingredientHy: ingredientHy,
    ingredientEn: ingredientEn,
    ingredientRu: ingredientRu
  }];

  // Handle type - can come from "Type Am", "Type En", "Type Ru" or just "Type"
  const typeValue = row.type || row.typeAm || row.typeEn || row.typeRu || '';
  
  // Build dish data object
  const dishData = {
    nameHy: row.nameHy || row.name || '',
    nameEn: row.nameEn || '',
    nameRu: row.nameRu || '',
    price: row.price ? Number(row.price) : 0,
    type: typeValue ? typeValue.toUpperCase().trim() : '',
    spiceLevel: typeValue && typeValue.toUpperCase() !== 'DESSERT' && row.spiceLevel 
      ? Number(row.spiceLevel) 
      : null,
    expirationDate: row.expirationDate ? Number(row.expirationDate) : null,
    orderBefore: row.orderBefore ? Number(row.orderBefore) : 1,
    minimumOrderCount: row.minimumOrderCount ? Number(row.minimumOrderCount) : 1,
    portionHy: row.portionHy || row.portionAm || '',
    portionEn: row.portionEn || '',
    portionRu: row.portionRu || '',
    ingridientsDto,
    dishTagDto,
    dishCuisineTagDto,
    dishAdditionDtoList: [],
    adjustableSpiceLevelDtoList: [],
    dietaryOptionDtoList: [],
    imageUrl: row.imageUrl || null,
    imageFileName: row.imageFileName || null
  };

  return dishData;
};

/**
 * Extracts images from a ZIP file
 * @param {File} zipFile - ZIP file containing images
 * @returns {Promise<Map>} Map of filename to File object
 */
export const extractImagesFromZip = async (zipFile) => {
  try {
    const zip = new JSZip();
    await zip.loadAsync(zipFile);
    const imageMap = new Map();
    
    // Process all files in parallel
    const promises = [];
    zip.forEach((relativePath, file) => {
      if (!file.dir) {
        // Check if it's an image file
        const extension = relativePath.split('.').pop().toLowerCase();
        const imageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
        
        if (imageExtensions.includes(extension)) {
          promises.push(
            file.async('blob').then(blob => {
              const fileName = relativePath.split('/').pop(); // Get just filename
              const imageFile = new File([blob], fileName, { type: blob.type });
              imageMap.set(fileName.toLowerCase(), imageFile);
            })
          );
        }
      }
    });
    
    await Promise.all(promises);
    return imageMap;
  } catch (error) {
    throw new Error(`Failed to extract images from ZIP: ${error.message}`);
  }
};

/**
 * Downloads an image from a URL
 * @param {string} url - Image URL
 * @param {number} timeout - Timeout in milliseconds (default: 10000)
 * @returns {Promise<File>} Image file
 */
export const downloadImageFromURL = async (url, timeout = 10000) => {
  return new Promise((resolve, reject) => {
    // Validate URL
    try {
      new URL(url);
    } catch {
      reject(new Error('Invalid URL'));
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    fetch(url, {
      signal: controller.signal,
      mode: 'cors'
    })
      .then(response => {
        clearTimeout(timeoutId);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        // Validate it's an image
        if (!blob.type.startsWith('image/')) {
          throw new Error('URL does not point to an image');
        }
        
        // Extract filename from URL or use default
        const urlParts = url.split('/');
        const fileName = urlParts[urlParts.length - 1].split('?')[0] || 'image.jpg';
        
        const imageFile = new File([blob], fileName, { type: blob.type });
        resolve(imageFile);
      })
      .catch(error => {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          reject(new Error('Image download timeout'));
        } else {
          reject(error);
        }
      });
  });
};

/**
 * Note: Duplicate checking by name is not required.
 * Dishes are only unique by ID, not by name.
 * Multiple dishes can have the same name.
 */

/**
 * Normalizes column names from various formats
 * @param {string} columnName - Column name to normalize
 * @returns {string} Normalized column name
 */
export const normalizeColumnName = (columnName) => {
  if (!columnName) return '';
  
  return columnName
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9]/g, '');
};

/**
 * Maps normalized column names to dish field names
 * Supports both export format and simplified format
 * @param {string} normalizedName - Normalized column name
 * @returns {string} Dish field name or null
 */
export const mapColumnToField = (normalizedName) => {
  const columnMap = {
    // Name fields (export format: "Name", "Name En", "Name Ru")
    'name': 'nameHy',
    'namehy': 'nameHy',
    'nameam': 'nameHy',
    'nameen': 'nameEn',
    'nameru': 'nameRu',
    // Price
    'price': 'price',
    // Type (export format: "Type Am", "Type En", "Type Ru" - but type is same for all)
    'type': 'type',
    'typeam': 'type',
    'typeen': 'type',
    'typeru': 'type',
    // Spice level (export format: "Spice Level")
    'spicelevel': 'spiceLevel',
    // Ingredients (export format: "Ingredients Am", "Ingredients En", "Ingredients Ru")
    'ingredientsam': 'ingredientHy',
    'ingredientshy': 'ingredientHy',
    'ingredienthy': 'ingredientHy',
    'ingredientam': 'ingredientHy',
    'ingredientsen': 'ingredientEn',
    'ingredienten': 'ingredientEn',
    'ingredientsru': 'ingredientRu',
    'ingredientru': 'ingredientRu',
    // Dish category/tag (export format: "Dish Category Am", "Dish Category En", "Dish Category Ru")
    'dishcategoryam': 'dishTagHy',
    'dishcategoryhy': 'dishTagHy',
    'dishcategoryen': 'dishTagEn',
    'dishcategoryru': 'dishTagRu',
    'dishtaghy': 'dishTagHy',
    'dishtagam': 'dishTagHy',
    'dishtagen': 'dishTagEn',
    'dishtagru': 'dishTagRu',
    // Cuisine (export format: "Cuisine Am", "Cuisine En", "Cuisine Ru")
    'cuisineam': 'cuisineHy',
    'cuisinehy': 'cuisineHy',
    'cuisineen': 'cuisineEn',
    'cuisineru': 'cuisineRu',
    // Portion (export format: "Portion Am", "Portion En", "Portion Ru")
    'portionam': 'portionHy',
    'portionhy': 'portionHy',
    'portionen': 'portionEn',
    'portionru': 'portionRu',
    // Dates and counts (not in export, but may be in import)
    'expirationdate': 'expirationDate',
    'orderbefore': 'orderBefore',
    'minimumordercount': 'minimumOrderCount',
    // Images (export format: "Image URL")
    'imageurl': 'imageUrl',
    'imagefilename': 'imageFileName',
    'image': 'imageUrl'
  };
  
  return columnMap[normalizedName] || null;
};
